import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth } from '../api/firebase';
import { onAuthChange, signInWithGoogle, signOut, ensureGuestToken, refreshToken } from '../api/firebase';
import { validateMembership } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [userData,    setUserData]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const prevUid = React.useRef(null);

  useEffect(() => {
    let mounted = true;

    // Wait for Firebase to check for a persisted session.
    // Once this resolves, the app is ready to render — token is set if user was
    // previously logged in, or null if completely new visitor (uses /tv endpoints).
    ensureGuestToken().finally(() => {
      if (mounted) setLoading(false);
    });

    // Token auto-refresh every 45 min
    const refreshInterval = setInterval(() => {
      if (auth.currentUser && !auth.currentUser.isAnonymous && mounted) {
        refreshToken(auth.currentUser).catch(() => {});
      }
    }, 45 * 60 * 1000);

    // Auth state listener — only fires for real (non-anonymous) users
    const unsub = onAuthChange(async (firebaseUser) => {
      if (!mounted) return;

      if (firebaseUser) {
        setUser(firebaseUser);
        setLoading(false); // ensure loading is cleared

        // Fetch membership in background
        try {
          const res = await validateMembership();
          if (mounted) setUserData(res?.data ?? null);
        } catch { if (mounted) setUserData(null); }

        prevUid.current = firebaseUser.uid;

        // Handle post-login redirect (e.g. guest clicked Subscribe → login → plans page)
        const postLoginRedirect = sessionStorage.getItem('sf_post_login_redirect');
        if (postLoginRedirect && mounted) {
          sessionStorage.removeItem('sf_post_login_redirect');
          // Small delay so state settles before navigation
          setTimeout(() => {
            window.location.href = postLoginRedirect;
          }, 300);
        }
      } else {
        if (mounted) {
          setUser(null);
          setUserData(null);
          setLoading(false);
        }
        prevUid.current = null;
      }
    });

    return () => { mounted = false; unsub(); clearInterval(refreshInterval); };
  }, []); // eslint-disable-line

  const handleSignIn = useCallback(async () => {
    try { await signInWithGoogle(); }
    catch (e) { console.error('Sign-in error:', e); }
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setUser(null);
    setUserData(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, userData, loading,
      isAuthenticated:     !!user,
      isPremium:           userData?.premiumuser === 1,
      showSubscribeButton: !!user && userData?.premiumuser !== 1,
      signIn:  handleSignIn,
      signOut: handleSignOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
